import React, {
  useState,
  useEffect,
  useCallback,
  useRef, ChangeEvent,
} from 'react';
import type { MultipleQueriesResponse } from '@algolia/client-search';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';
import { SearchClient } from 'algoliasearch/lite';
import { TextInput, Link } from '@printdeal/ui-library';
import { debounce } from 'es-toolkit';
import { ErrorManagement } from '@printdeal/error-management/react';
import { dispatchDataLayerEventOnSearchInteractionGA4 } from './AlgoliaSearch.datalayer';
import {
  SearchResponsePerIndex,
  AlgoliaSearchStyling,
  IndexSearchResult,
} from './types';
import { useReadyForCypress } from '../../hooks/useReadyForCypress';

const ALGOLIA_DEBOUNCE_TIME_MS = 200;

interface AlgoliaSearchIndicesProp {
  name: string;
  hitsPerPage: number;
}
interface AlgoliaSearchProps {
  onSearchResponseSuccess?: (searchResponses: SearchResponsePerIndex[], searchText: string) => void;
  id?: string;
  name?: string;
  placeholder: string;
  placeholderLoading: string;
  value?: string;
  className?: string;
  indices: AlgoliaSearchIndicesProp[];
  searchClient: SearchClient;
  clearSearch?: boolean;
  onSearchQueryChange?: (query: string) => void;
  onSearchValueChange?: (query: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  locale?: string;
  styling?: AlgoliaSearchStyling;
  autoComplete?: string;
  allowHotjar: boolean;
}

const AlgoliaSearch = ({
  onSearchResponseSuccess = () => { },
  id = 'search',
  name = 'search',
  placeholder,
  placeholderLoading,
  value = '',
  className = '',
  searchClient,
  indices,
  clearSearch = false,
  onSearchQueryChange = () => { },
  onSearchValueChange = () => { },
  onFocus = () => { },
  onBlur = () => { },
  locale = '',
  allowHotjar = true,
  ...rest
}: AlgoliaSearchProps) => {
  const [searchText, setSearchText] = useState<string>(value);
  const [searchResponses, setSearchResponses] = useState<Array<SearchResponsePerIndex>>([]);
  const searchTextRef = useRef<string>();

  const clearSearchResults = () => {
    setSearchText('');
    setSearchResponses([]);
  };

  const readyForCypressId = useReadyForCypress('search-input');

  useEffect(() => {
    if (clearSearch) {
      clearSearchResults();
    }
  }, [clearSearch]);

  useEffect(() => {
    onSearchResponseSuccess(searchResponses, searchText);
  }, [searchResponses]);

  searchTextRef.current = searchText;
  const manageResults = (results: IndexSearchResult[]) => {
    const responsePerIndex = indices.map((idx: SearchResponsePerIndex) => {
      const index: SearchResponsePerIndex = { ...idx };
      index.searchResults = results.find((resObj) => resObj.index === index.name);
      return index;
    });
    setSearchResponses(responsePerIndex);
  };

  const queryAlgolia = useCallback(debounce((query: string) => {
    const multiQueryArray = indices.map((idx) => ({
      indexName: idx.name,
      query,
      params: {
        hitsPerPage: idx.hitsPerPage,
      },
      clickAnalytics: true,
      getRankingInfo: true,
      analyticsTags: [
        locale,
      ],
      filters: `locale:'${locale}'`,
    }));

    searchClient
      .search(multiQueryArray)
      .then((response: MultipleQueriesResponse<unknown>) => {
        const { results } = response;
        if (searchTextRef.current) {
          manageResults(results as IndexSearchResult[]);
        }
      }).catch((error) => {
        ErrorManagement.exception({
          exception: error,
          tags: {
            component: 'AlgoliaSearch',
            fileLocation: 'components-AlgoliaSearch',
            featureFunction: 'validate-response-results',
          },
          fingerprint: ['AlgoliaSearch-validate-response-results'],
        });
      });
  }, ALGOLIA_DEBOUNCE_TIME_MS), []);

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target: { value: query } } = event;
    onSearchValueChange(query);
    if (query) {
      setSearchText(query);
      queryAlgolia(query);
    } else {
      clearSearchResults();
    }
    if (onSearchQueryChange) {
      onSearchQueryChange(query);
    }
  };

  useEffect(() => {
    if (value) {
      setSearchText(value);
      queryAlgolia(value);

      onSearchQueryChange(value);
    }
  }, [value]);

  const handleSearchBarClick = (clickLocation: string) => {
    dispatchDataLayerEventOnSearchInteractionGA4(clickLocation);
  };

  return (
    <TextInput
      variant="primary"
      className={clsx('pd-w-full', className)}
      onChange={handleSearchInputChange}
      onClick={() => handleSearchBarClick('search_field')}
      onFocus={onFocus}
      onBlur={onBlur}
      id={id}
      type="search"
      name={name}
      placeholder={placeholder}
      value={searchText}
      data-cy={readyForCypressId}
      iconAfter={(
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link
          as="button"
          variant="secondary"
          type="submit"
          iconBefore="search"
          onClick={() => handleSearchBarClick('magnifying_glass_icon')}
        >
          <span className="pd-sr-only">
            <FormattedMessage id="search.bar.submit" defaultMessage="Search" />
          </span>
        </Link>
      )}
      /**
       * Allow Hotjar to track the search input field
       */
      data-hj-allow={allowHotjar}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    />
  );
};

export default AlgoliaSearch;
